/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import {
  BrowserCacheLocation,
  Configuration,
  LogLevel,
} from "@azure/msal-browser";
import { environment } from "../environments/environment";

export const b2cPolicies = {
  names: {
    signUpSignIn: "B2C_1_Financial_Advisor_Signup_and_Sign_In",
    // editProfile: "b2c_1_edit_profile_v2"
  },
  authorities: {
    signUpSignIn: {
      authority:
        "https://" + environment.mslConfig.tenantName + ".b2clogin.com/" + environment.mslConfig.tenantName + ".onmicrosoft.com/" + environment.mslProfile,
      // "https://fispokedev.b2clogin.com/fispokedev.onmicrosoft.com/" + environment.mslProfile,
    },
    // editProfile: {
    //   authority:
    //     "https://fispokedev.b2clogin.com/fispokedev.onmicrosoft.com/b2c_1_edit_profile_v2",
    // },
  },
  authorityDomain: environment.mslConfig.tenantName + ".b2clogin.com",
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: environment.mslConfig.clientId, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority,
    knownAuthorities: [b2cPolicies.authorityDomain],
    redirectUri: environment.mslConfig.redirectUri,
    postLogoutRedirectUri: "/firmlogin", // Points to window.location.origin by default.
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        console.log(message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Build the post-logout redirect URI based on the current session's firmname + roleType.
 *
 * Pattern:
 *   <env.postLogoutRedirectUri>/<firmname>            for non-advisor roles (admin, investor, none)
 *   <env.postLogoutRedirectUri>/<firmname>/adv        for advisor roleType (so the next login re-enters
 *                                                     the SSO auto-discovery flow on /firmlogin/<firm>/adv)
 *
 * Reads firmname + roleType from localStorage, both of which are set on first login in
 * firm-login.component.ts:ngOnInit. Safe to call from MSAL config bootstrap, sidebar / header
 * logout handlers, jwt interceptor error path, and app-level error handlers.
 *
 * Defensive cache: if a re-entrant caller (e.g. firm-login.component re-mounting on /firmlogin
 * with no token mid-logout) invokes this helper after localStorage has been partially wiped,
 * fall back to the last successfully computed URI instead of regressing to /firmlogin/fispoke.
 * Cache is per-tab (module scope) and is replaced on every successful computation.
 */
let _lastComputedPostLogoutUri: string | null = null;

/**
 * MSAL only processes redirect responses on the configured redirectUri path.
 * If the router moved us to /firmlogin/... before handleRedirect runs, relocate
 * back to redirectUri while preserving the B2C hash/query.
 */
export function relocatePendingB2cRedirect(redirectUri: string): boolean {
  if (!hasPendingB2cRedirect()) {
    return false;
  }
  try {
    const configured = new URL(redirectUri);
    const current = new URL(window.location.href);
    const normalize = (path: string) => path.replace(/\/$/, '') || '/';
    if (normalize(current.pathname) === normalize(configured.pathname)) {
      return false;
    }
    const target =
      configured.origin +
      configured.pathname +
      current.search +
      current.hash;
    window.location.replace(target);
    return true;
  } catch {
    return false;
  }
}

/** B2C auth callback still in URL (hash or query). */
export function hasPendingB2cRedirect(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  return (
    hash.includes('code=') ||
    hash.includes('error=') ||
    search.includes('code=') ||
    search.includes('error=')
  );
}

/**
 * B2C response still sitting unconsumed in the URL fragment.
 *
 * Deliberately narrower than hasPendingB2cRedirect(): MSAL replies land in the fragment
 * (response_mode=fragment), so this ignores `?code=` in the query string, which belongs to
 * the separate credit-card OAuth callback (see consumeAuthCodeFromUrl in app.component).
 * Only use this for checks that act immediately — the broader helper would hijack that flow.
 */
export function hasUnconsumedB2cHashResponse(): boolean {
  const hash = window.location.hash;
  return hash.includes('code=') || hash.includes('error=');
}

/** MSAL redirect/logout still in flight (includes OAuth state param). */
export function hasPendingMsalInteraction(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  return hasPendingB2cRedirect() || hash.includes('state=') || search.includes('state=');
}

const SET_PASSWORD_POLICY_KEY = 'setPasswordPolicy';
const SET_PASSWORD_TOKEN_KEY = 'setPasswordToken';

export function isSetPasswordRoute(pathname?: string): boolean {
  return (pathname ?? window.location.pathname).includes('/setpassword/');
}

/** Persist B2C policy before set-password loginRedirect so handleRedirect uses the right authority. */
export function markSetPasswordFlow(policyName: string, token?: string): void {
  sessionStorage.setItem(SET_PASSWORD_POLICY_KEY, policyName);
  if (token) {
    sessionStorage.setItem(SET_PASSWORD_TOKEN_KEY, token);
  }
}

export function isSetPasswordFlow(): boolean {
  return !!sessionStorage.getItem(SET_PASSWORD_POLICY_KEY);
}

export function getSetPasswordToken(): string | null {
  return sessionStorage.getItem(SET_PASSWORD_TOKEN_KEY);
}

export function clearSetPasswordFlow(): void {
  sessionStorage.removeItem(SET_PASSWORD_POLICY_KEY);
  sessionStorage.removeItem(SET_PASSWORD_TOKEN_KEY);
}

const SET_PASSWORD_ATTEMPTED_KEY = 'setPasswordAttempted';

/** After B2C password-reset callback, block re-entry to B2C from the same link. */
export function markSetPasswordAttempted(token: string): void {
  sessionStorage.setItem(SET_PASSWORD_ATTEMPTED_KEY, token);
}

export function wasSetPasswordAttempted(token: string): boolean {
  return sessionStorage.getItem(SET_PASSWORD_ATTEMPTED_KEY) === token;
}

export function clearSetPasswordAttempted(): void {
  sessionStorage.removeItem(SET_PASSWORD_ATTEMPTED_KEY);
}

const SILENT_B2C_RECOVERY_KEY = 'silentB2cRecovery';

/**
 * Mark that the redirect to /firmlogin was triggered by an unusable B2C callback
 * (e.g. a bookmarked authorize URL replayed outside a loginRedirect() we initiated),
 * not by a plain unauthenticated visit. firm-login.component consumes this to skip
 * forcing prompt=login, so the still-warm B2C session cookie completes silently
 * instead of asking for credentials a second time.
 */
export function markSilentB2cRecovery(): void {
  sessionStorage.setItem(SILENT_B2C_RECOVERY_KEY, '1');
}

/** One-shot read: true if this firm-login mount followed a silent-recovery redirect. */
export function consumeSilentB2cRecovery(): boolean {
  const wasSet = !!sessionStorage.getItem(SILENT_B2C_RECOVERY_KEY);
  sessionStorage.removeItem(SILENT_B2C_RECOVERY_KEY);
  return wasSet;
}

const SET_PASSWORD_OAUTH_CALLBACK_KEY = 'setPasswordOAuthCallback';
const MSAL_REDIRECT_HANDLE_DONE_KEY = 'msalRedirectHandleDone';

export function markSetPasswordOAuthCallback(): void {
  sessionStorage.setItem(SET_PASSWORD_OAUTH_CALLBACK_KEY, '1');
}

export function consumeSetPasswordOAuthCallback(): boolean {
  if (sessionStorage.getItem(SET_PASSWORD_OAUTH_CALLBACK_KEY) === '1') {
    sessionStorage.removeItem(SET_PASSWORD_OAUTH_CALLBACK_KEY);
    return true;
  }
  return false;
}

export function markRedirectHandleDone(): void {
  sessionStorage.setItem(MSAL_REDIRECT_HANDLE_DONE_KEY, '1');
}

export function isRedirectHandleDone(): boolean {
  return sessionStorage.getItem(MSAL_REDIRECT_HANDLE_DONE_KEY) === '1';
}

export function clearRedirectHandleDone(): void {
  sessionStorage.removeItem(MSAL_REDIRECT_HANDLE_DONE_KEY);
}

export function isSetPasswordFlowForToken(token: string): boolean {
  return isSetPasswordFlow() && getSetPasswordToken() === token;
}

export function getSetPasswordTokenFromPath(pathname?: string): string | null {
  const match = (pathname ?? window.location.pathname).match(
    /\/setpassword\/([^/]+)/
  );
  return match?.[1] ?? null;
}

/** True when app should finish reset and send user to firmlogin (not start B2C again). */
export function shouldCompleteSetPasswordFlow(): boolean {
  if (!isSetPasswordFlow()) {
    return false;
  }
  if (consumeSetPasswordOAuthCallback()) {
    return true;
  }
  const flowToken = getSetPasswordToken();
  const pathToken = getSetPasswordTokenFromPath();
  return !!flowToken && !!pathToken && flowToken === pathToken;
}

/** Apply stored password-reset policy before MSAL consumes the B2C callback. */
export function applyPendingSetPasswordAuthority(): boolean {
  const policy = sessionStorage.getItem(SET_PASSWORD_POLICY_KEY);
  if (!policy || !hasPendingB2cRedirect()) {
    return false;
  }
  const tenantName = environment.mslConfig.tenantName;
  msalConfig.auth.authority = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/${policy}`;
  msalConfig.auth.knownAuthorities = [tenantName + '.b2clogin.com'];
  markSetPasswordOAuthCallback();
  return true;
}

const FRESH_LOGIN_CALLBACK_KEY = 'freshLoginCallback';

/** Set when handleRedirect returns a new account (post-MFA). Survives until consumed. */
export function markFreshLoginCallback(): void {
  sessionStorage.setItem(FRESH_LOGIN_CALLBACK_KEY, '1');
}

export function isFreshLoginCallback(): boolean {
  if (isRoleSwitchInProgress()) {
    return false;
  }
  return sessionStorage.getItem(FRESH_LOGIN_CALLBACK_KEY) === '1';
}

export function consumeFreshLoginCallback(): boolean {
  if (sessionStorage.getItem(FRESH_LOGIN_CALLBACK_KEY) === '1') {
    sessionStorage.removeItem(FRESH_LOGIN_CALLBACK_KEY);
    return true;
  }
  return false;
}

const ROLE_SWITCH_UNTIL_KEY = 'roleSwitchUntil';
const ROLE_SWITCH_MS = 12000;

/** Survives sessionStorage.clear(); blocks stale-session logout while switch settles. */
export function isRoleSwitchInProgress(): boolean {
  const until = localStorage.getItem(ROLE_SWITCH_UNTIL_KEY);
  if (!until) {
    return false;
  }
  if (Date.now() > Number(until)) {
    localStorage.removeItem(ROLE_SWITCH_UNTIL_KEY);
    return false;
  }
  return true;
}

export function startRoleSwitch(): void {
  sessionStorage.removeItem(FRESH_LOGIN_CALLBACK_KEY);
  localStorage.setItem(ROLE_SWITCH_UNTIL_KEY, String(Date.now() + ROLE_SWITCH_MS));
}

export function endRoleSwitch(): void {
  localStorage.removeItem(ROLE_SWITCH_UNTIL_KEY);
}

/** Recover dashboard paths mistakenly routed under /firmlogin/:firm/… */
export function getMalformedFirmloginRecoveryPath(pathname: string): string | null {
  const match = pathname.match(/^\/firmlogin\/[^/]+\/(.+)$/);
  if (!match) {
    return null;
  }
  const segment = match[1].toLowerCase();
  if (segment === 'adv') {
    return null;
  }
  if (segment.includes('dashboard') || segment.includes('advisor')) {
    return '/' + match[1];
  }
  return null;
}

export function getPostLogoutRedirectUri(): string {
  const firmname = localStorage.getItem("firmname") || "";
  const roleType = localStorage.getItem("roleType") || "";
  if (!firmname) {
    return (
      _lastComputedPostLogoutUri ||
      environment.mslConfig.postLogoutRedirectUri + "/" + firmname
    );
  }
  const base = environment.mslConfig.postLogoutRedirectUri + "/" + firmname;
  const uri = roleType === "adv" ? base + "/adv" : base;
  _lastComputedPostLogoutUri = uri;
  return uri;
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    "https://" + environment.mslConfig.tenantName + ".onmicrosoft.com/" + environment.mslConfig.tenantId + "/User.Read",
    "https://" + environment.mslConfig.tenantName + ".onmicrosoft.com/" + environment.mslConfig.tenantId + "/User.Write",
  ],
};
